/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react'
import cx from 'classnames'
import { Position, Classes } from '@blueprintjs/core'
import { Suggest, ISuggestProps, IItemRendererProps } from '@blueprintjs/select'

import css from './Select.css'
import { Button } from '../../components/Button/Button'
import { Utils } from '../../core/Utils'
import { Text } from '../../components/Text/Text'
import { Popover } from '../../components/Popover/Popover'
import { Icon, IconProps } from '@harnessio/icons'

export interface SelectOption {
  label: string
  value: string | number | symbol
  icon?: IconProps
  rightIcon?: IconProps
}

export enum SelectSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

type Props = ISuggestProps<SelectOption>

const Loading = Symbol('loading')

export interface SelectProps
  extends Omit<
    Props,
    | 'popoverProps'
    | 'inputValueRenderer'
    | 'itemRenderer'
    | 'onItemSelect'
    | 'query'
    | 'selectedItem'
    | 'items'
    | 'activeItem'
    | 'onActiveItemChange'
  > {
  inputValueRender?: Props['inputValueRenderer']
  itemRenderer?: Props['itemRenderer']
  onChange?: Props['onItemSelect']
  value?: Props['selectedItem']
  size?: SelectSize
  items: Props['items'] | (() => Promise<Props['items']>)
  allowCreatingNewItems?: boolean
  newItemRenderer?: (query: string, clickHandler?: React.MouseEventHandler<Element>) => React.ReactNode
  name?: string
  whenPopoverClosed?: (node: HTMLElement) => void
  addClearBtn?: boolean
  usePortal?: boolean
  popoverClassName?: string
  onQueryChange?: Props['onQueryChange']
  addTooltip?: boolean
  borderless?: boolean
  loadingItems?: boolean
}

function getIconSizeFromSelect(size: SelectSize = SelectSize.Medium) {
  let iconSize = 16
  if (size === SelectSize.Small) {
    iconSize = 12
  } else if (size === SelectSize.Large) {
    iconSize = 18
  }
  return iconSize
}

export function defaultItemRenderer(
  item: SelectOption,
  props: IItemRendererProps,
  size: SelectSize = SelectSize.Medium
): JSX.Element | null {
  if (!props.modifiers.matchesPredicate) {
    return null
  }

  if (item.value === Loading) {
    return (
      <li key="loading" className={cx(css.menuItem, css.loading)}>
        Loading results...
      </li>
    )
  }

  return (
    <li
      key={item.value.toString()}
      className={cx(
        css.menuItem,
        {
          [css.active]: props.modifiers.active,
          [css.disabled]: props.modifiers.disabled
        },
        { [css.menuItemSizeSmall]: size === SelectSize.Small },
        { [css.menuItemSizeLarge]: size === SelectSize.Large },
        { [css.rightIcon]: item.rightIcon }
      )}
      onClick={props.handleClick}>
      {item.icon ? <Icon size={getIconSizeFromSelect(size)} {...item.icon} /> : null}
      <Text className={css.menuItemLabel} lineClamp={1}>
        {item.label}
      </Text>
      {item.rightIcon ? <Icon size={getIconSizeFromSelect(size)} {...item.rightIcon} /> : null}
    </li>
  )
}

export function createNewItemFromQuery(query: string): SelectOption {
  return { label: query, value: query }
}

export function Select(props: SelectProps): React.ReactElement {
  const [query, setQuery] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [items, setItems] = React.useState(Array.isArray(props.items) ? props.items : [])
  const {
    onChange,
    value,
    size,
    itemRenderer,
    addClearBtn,
    whenPopoverClosed,
    popoverClassName = '',
    resetOnSelect = true,
    resetOnClose = true,
    addTooltip = false,
    borderless = false,
    loadingItems,
    ...rest
  } = props
  const [item, setItem] = React.useState<SelectOption | undefined | null>(undefined)

  const showClearBtn =
    !!addClearBtn &&
    value !== null &&
    value !== undefined &&
    (value as any) !== '' &&
    value?.value !== undefined &&
    value?.value !== null &&
    value?.value !== ''

  React.useEffect(() => {
    setItem(value)
  }, [value])

  function handleItemSelect(item: SelectOption) {
    if (item.value === Loading) {
      return
    }
    if (typeof onChange === 'function') {
      onChange(item)
    } else {
      setItem(item)
    }
  }

  React.useEffect(() => {
    if (Array.isArray(props.items)) {
      setItems(props.items)
    } else if (typeof props.items === 'function') {
      setLoading(true)
      const promise = props.items()

      if (typeof promise.then === 'function') {
        promise.then(results => {
          setItems(results)
          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    }
  }, [props.items])

  function createNewItemRenderer(
    query: string,
    _active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>
  ): JSX.Element | undefined {
    if (loading || loadingItems) {
      return (
        <li key="loading" className={cx(css.menuItem, css.loading)}>
          Loading results...
        </li>
      )
    }

    const isExactQueryElPresent = items.some(item => item.label === query)
    if (!loading)
      return (
        <React.Fragment>
          {items.filter(item => item.label.toString().toLowerCase().includes(query.toLowerCase())).length === 0 ? (
            <div className={css.noResultsFound}>No Match Found</div>
          ) : null}
          {props.allowCreatingNewItems && !isExactQueryElPresent ? (
            props.newItemRenderer ? (
              props.newItemRenderer?.(query, handleClick)
            ) : (
              <Button
                intent="primary"
                minimal
                text={query}
                icon="plus"
                className={css.createNewItemButton}
                onClick={handleClick as React.MouseEventHandler<Element>}
              />
            )
          ) : null}
        </React.Fragment>
      )
  }
  const renderSuggestComponent = () => (
    <Suggest
      inputValueRenderer={opt => opt.label?.toString()}
      itemRenderer={(item: SelectOption, props: IItemRendererProps) =>
        itemRenderer?.(item, props) || defaultItemRenderer(item, props, size)
      }
      itemListPredicate={(query, items) =>
        items.filter(item => item.label.toString().toLowerCase().includes(query.toLowerCase()))
      }
      createNewItemFromQuery={props.createNewItemFromQuery || createNewItemFromQuery}
      createNewItemRenderer={props.createNewItemRenderer || createNewItemRenderer}
      noResults={<NoMatch />}
      {...rest}
      inputProps={{
        onChange(e: React.ChangeEvent<HTMLInputElement>) {
          setQuery(e.target.value)
        },
        value: query,
        placeholder: Utils.getSelectComponentPlaceholder(rest?.inputProps?.placeholder),
        leftElement: item?.icon ? <Icon size={getIconSizeFromSelect(size)} {...item?.icon} /> : undefined,
        rightElement: (
          <>
            {!props.disabled && showClearBtn ? (
              <Icon
                name="main-delete"
                onClick={(e: React.MouseEvent<HTMLHeadingElement, MouseEvent>) => {
                  e.preventDefault()
                  handleItemSelect({ value: '', label: '' })
                }}
                size={14}
                padding={{ top: 'small', right: 'xsmall', bottom: 'small' }}
              />
            ) : null}
            <Icon
              name={'chevron-down'}
              onClick={e => {
                const input = e.currentTarget.parentElement?.previousElementSibling as HTMLInputElement
                input?.focus()
              }}
              size={14}
              padding={size === SelectSize.Small ? 'xsmall' : 'small'}
            />
          </>
        ),
        small: size === SelectSize.Small,
        large: size === SelectSize.Large,
        name: props.name,
        ...props.inputProps
      }}
      resetOnSelect={resetOnSelect}
      resetOnClose={resetOnClose}
      items={loading ? [{ label: 'Loading...', value: Loading }] : items}
      selectedItem={item}
      onItemSelect={handleItemSelect}
      query={query}
      popoverProps={{
        targetTagName: 'div',
        fill: true,
        usePortal: !!props.usePortal,
        minimal: true,
        position: Position.BOTTOM_LEFT,
        className: cx(css.main, { [css.borderless]: borderless }),
        popoverClassName: cx(css.popover, popoverClassName),
        onClosed: whenPopoverClosed,
        modifiers: {
          preventOverflow: {
            // This is required to always attach the portal to the start of the reference instead of the middle
            escapeWithReference: !!props.usePortal
          },
          offset: {
            // This is required to offset the portal after it is attached to the reference.
            // By default the portal is positioned at top: 0, left:0 wrt it's reference
            offset: props.usePortal ? '1 2' : 0
          }
        }
      }}
    />
  )
  const tooltipContent = item?.label ? (
    <div className={css.tooltipContainer} color="white">
      {item.label}
    </div>
  ) : (
    ''
  )
  return addTooltip ? (
    <Popover
      boundary="viewport"
      interactionKind="hover"
      content={tooltipContent}
      isDark={true}
      fill={true}
      popoverClassName={Classes.DARK}
      position="bottom">
      {renderSuggestComponent()}
    </Popover>
  ) : (
    renderSuggestComponent()
  )
}

export function NoMatch(): React.ReactElement {
  return <li className={cx(css.menuItem, css.disabled)}>No matching results found</li>
}
